import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, Globe, Lightbulb, Loader2, MessageSquare, Search, Target, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { getSharedMIReport, getSharedJDAnalysis, getSharedDiscoveryReport, getSharedSourcingPlan, getSharedCombined } from '../services/api';
import { ThemeProvider } from '../context/ThemeContext';
import { JDAnalysisPanel } from '../components/JDAnalysisPanel';
import { MIReportView } from '../components/mi/MIReportView';
import { DiscoveryReportView } from '../components/discovery/DiscoveryReportView';
import { SourcingPlanDisplay } from '../components/SourcingStrategyTab';
import { PdfButton } from '../components/report/ReportBlocks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const shareQ = (token) => `?share=${encodeURIComponent(token)}`;
const workLabel = (w) => ({ remote: 'Remote', onsite: 'Onsite / Hybrid' }[w] || w);
const place = (d) => [d.city, d.country].filter(Boolean).join(', ');

// Each shared page renders the same report component as the job page, and its PDF button opens the same PDF
const useShared = (fetcher) => {
  const { shareToken } = useParams();
  const [state, setState] = useState({ data: null, loading: true, error: false });
  useEffect(() => {
    fetcher(shareToken).then((data) => setState({ data, loading: false, error: false }))
      .catch(() => setState({ data: null, loading: false, error: true }));
  }, [shareToken, fetcher]);
  return { ...state, token: shareToken };
};

const SharedState = ({ loading, message }) => (
  <ThemeProvider>
    <div className="min-h-screen bg-background flex items-center justify-center">
      {loading ? <Loader2 className="h-8 w-8 animate-spin text-primary" data-testid="shared-loading" /> : (
        <Card className="max-w-md"><CardContent className="pt-6 text-center" data-testid="shared-error">
          <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">{message}</p>
        </CardContent></Card>
      )}
    </div>
  </ThemeProvider>
);

const SharedShell = ({ Icon, brand, title, meta = [], actions, children, testid }) => (
  <ThemeProvider>
    <div className="min-h-screen bg-background" data-testid={testid}>
      <header className="border-b bg-muted/30">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex items-center gap-2 mb-1">
            <Icon className="h-5 w-5 text-primary" />
            <span className="text-sm font-semibold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>HireFast AI — {brand}</span>
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="shared-job-title">{title}</h1>
          {meta.some(Boolean) && <p className="mt-1 text-sm text-muted-foreground" data-testid="shared-job-meta">{meta.filter(Boolean).join(' · ')}</p>}
          {actions && <div className="mt-3 flex items-center gap-2 flex-wrap">{actions}</div>}
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-6">
        {children}
        <p className="text-xs text-center text-muted-foreground py-6">Generated by HireFast AI by TekWissen</p>
      </main>
    </div>
  </ThemeProvider>
);

export function SharedMIReportPage() {
  const s = useShared(getSharedMIReport);
  if (s.loading || s.error) return <SharedState loading={s.loading} message="Report not found or link expired" />;
  const d = s.data;
  return (
    <SharedShell Icon={Globe} brand="Market Intelligence" title={d.job_title} meta={[d.job_location, workLabel(d.work_type), d.company_name]} testid="shared-mi-page">
      <MIReportView data={d} pdfUrl={`${BACKEND_URL}/api/mi/report/${d.report_id}/pdf${shareQ(s.token)}`} />
    </SharedShell>
  );
}

export function SharedJDAnalysisPage() {
  const s = useShared(getSharedJDAnalysis);
  if (s.loading || s.error) return <SharedState loading={s.loading} message="Analysis not found or link expired" />;
  const d = s.data;
  return (
    <SharedShell Icon={Target} brand="JD Intelligence" title={d.job_title} meta={[d.job_type, place(d), d.client_name]} testid="shared-jd-page">
      <JDAnalysisPanel analysis={d.jd_analysis} isLoading={false} pdfUrl={`${BACKEND_URL}/api/jobs/${d.job_id}/jd-pdf${shareQ(s.token)}`} />
    </SharedShell>
  );
}

export function SharedDiscoveryPage() {
  const s = useShared(getSharedDiscoveryReport);
  if (s.loading || s.error) return <SharedState loading={s.loading} message="Discovery questions not found or link expired" />;
  const d = s.data;
  return (
    <SharedShell Icon={MessageSquare} brand="Discovery Call Questions" title={d.job_title} meta={[d.company_name]} testid="shared-discovery-page">
      <DiscoveryReportView data={d.questions} pdfUrl={`${BACKEND_URL}/api/discovery/${d.discovery_id}/pdf${shareQ(s.token)}`} />
    </SharedShell>
  );
}

export function SharedSourcingPage() {
  const s = useShared(getSharedSourcingPlan);
  if (s.loading || s.error) return <SharedState loading={s.loading} message="Sourcing plan not found or link expired" />;
  const d = s.data;
  return (
    <SharedShell Icon={Search} brand="Smart Sourcing Strategy" title={d.job_title} meta={[d.job_location, workLabel(d.work_type), d.category]} testid="shared-sourcing-page">
      <SourcingPlanDisplay plan={d.plan} planId={d.plan_id} jobTitle={d.job_title} pdfUrl={`${BACKEND_URL}/api/sourcing/plan/${d.plan_id}/pdf${shareQ(s.token)}`} />
    </SharedShell>
  );
}

const InterviewQuestions = ({ data }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-2">
      <Users className="h-5 w-5 text-blue-500" />
      <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Interview Questions</h3>
      <Badge variant="secondary" className="text-xs">{data.total} Questions</Badge>
    </div>
    {data.questions.map((q, i) => (
      <div key={i} className="p-3 rounded-lg border bg-muted/10" data-testid={`combined-interview-question-${i}`}>
        <div className="flex items-start gap-2">
          <Badge className="bg-blue-500 text-white text-[10px] shrink-0 mt-0.5">Q{q.order || i + 1}</Badge>
          <div>
            <p className="text-sm font-medium">{q.question}</p>
            <div className="flex items-center gap-2 mt-1">
              {q.category && <Badge variant="secondary" className="text-[10px]">{q.category}</Badge>}
              {q.time_limit && <span className="text-[10px] text-muted-foreground">{q.time_limit}s</span>}
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const SECTIONS = {
  jd: ['JD Intelligence', Target, (d, q, c) => <JDAnalysisPanel analysis={d} isLoading={false} pdfUrl={`${BACKEND_URL}/api/jobs/${c.job_id}/jd-pdf${q}`} />],
  mi: ['Market Intelligence', Globe, (d, q) => <MIReportView data={d} pdfUrl={`${BACKEND_URL}/api/mi/report/${d.report_id}/pdf${q}`} />],
  discovery: ['Discovery Call Questions', Lightbulb, (d, q) => <DiscoveryReportView data={d.questions} pdfUrl={`${BACKEND_URL}/api/discovery/${d.discovery_id}/pdf${q}`} />],
  sourcing: ['Smart Sourcing Strategy', Search, (d, q, c) => <SourcingPlanDisplay plan={d.plan} planId={d.plan_id} jobTitle={c.job_title} pdfUrl={`${BACKEND_URL}/api/sourcing/plan/${d.plan_id}/pdf${q}`} />],
  interview: ['AI Interview Questions', Users, (d) => <InterviewQuestions data={d} />],
};

export function SharedCombinedPage() {
  const s = useShared(getSharedCombined);
  if (s.loading || s.error) return <SharedState loading={s.loading} message="Share link not found or expired" />;
  const c = s.data;
  const shown = (c.sections || []).filter((k) => SECTIONS[k] && c.data?.[k]);
  const toc = shown.map((k) => {
    const [label, Icon] = SECTIONS[k];
    return (
      <a key={k} href={`#section-${k}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border bg-background text-xs font-medium hover:bg-muted transition-colors" data-testid={`combined-toc-${k}`}>
        <Icon className="h-3 w-3" /> {label}
      </a>
    );
  });
  const actions = shown.length > 0 && (
    <>{toc}<PdfButton url={`${BACKEND_URL}/api/shared/combined/${s.token}/pdf`} testid="combined-download-pdf" label="Download full PDF" /></>
  );
  return (
    <SharedShell Icon={Target} brand="Complete Job Intelligence" title={c.job_title} meta={[c.job_type, place(c), c.client_name]} actions={actions} testid="shared-combined-page">
      <div className="space-y-12">
        {shown.map((k) => <section key={k} id={`section-${k}`} className="scroll-mt-6" data-testid={`combined-section-${k}`}>{SECTIONS[k][2](c.data[k], shareQ(s.token), c)}</section>)}
        {!shown.length && <p className="text-sm text-muted-foreground text-center py-10" data-testid="combined-empty">Nothing has been generated yet for the shared sections.</p>}
      </div>
    </SharedShell>
  );
}
